import { getApiOrigin } from "@utils/apiOrigin";
import { facilityService } from "./facilityServices";

const DEV_LICENSE_HEARTBEAT_PATH = "/dev/license/heartbeat";

/** Outcome of probing Rules API license gate via the same GET as E2E preflight. */
export type RulesLicensePreflightResult =
  | "active"
  | "inactive"
  | "unauthenticated"
  | "error";

/**
 * GET my-facilities (authenticated) via facilityServices cache/in-flight dedupe.
 * HTTP 402 with LICENSE_INACTIVE means the org subscription is inactive;
 * HTTP 401 means the session token is invalid or expired (sign in again).
 */
export async function runRulesLicensePreflight(): Promise<RulesLicensePreflightResult> {
  if (!getApiOrigin().replace(/\/+$/, "")) {
    return "error";
  }

  try {
    await facilityService.getMyFacilities({ bypassCache: true });
    return "active";
  } catch (error) {
    const httpStatus = (error as { httpStatus?: number }).httpStatus;
    if (httpStatus === 402) {
      return "inactive";
    }
    if (httpStatus === 401) {
      return "unauthenticated";
    }
    return "error";
  }
}

/**
 * Dashboard license gate must not block routes that cannot call Rules yet.
 * - Billing: allow payment update while license inactive.
 * - No-org Hub session: SSO/email signup creates MCM identity before Rules
 *   user mapping exists; my-facilities 401 is expected, not an expired session.
 */
export function shouldSkipRulesLicensePreflight(input: {
  isBillingSubRoute: boolean;
  isNoOrgSession: boolean;
}): boolean {
  return Boolean(input.isBillingSubRoute || input.isNoOrgSession);
}

/**
 * Ask Rules to run the same MCM `get_license_and_features` + `LicenseState.set_active` as the
 * background thread (POST /dev/license/heartbeat). Path is not under /api/, so it bypasses
 * LicenseMiddleware—safe to call while the license is still marked inactive in-process.
 *
 * No JWT required (dev route). Returns true when Rules responds 200 with `{ ok: true }`.
 */
export async function runRulesLicenseHeartbeatRefresh(): Promise<boolean> {
  const base = getApiOrigin().replace(/\/+$/, "");
  if (!base) {
    return false;
  }
  try {
    const response = await fetch(`${base}${DEV_LICENSE_HEARTBEAT_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      return false;
    }
    const data = (await response.json()) as { ok?: boolean };
    return data.ok === true;
  } catch {
    return false;
  }
}
